import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { api, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * QR redeem page — visited when a user scans the QR code on their phone.
 *
 * Implementation note: under React.StrictMode the effect double-mounts. To
 * stop the first mount from consuming the one-time token then the second
 * mount racing in and getting `token_used`, we cache the in-flight promise
 * at module scope keyed by `token`. Both mounts await the SAME promise.
 */
const inflight = new Map(); // token → Promise<{redirect, token, user}>

function redeemOnce(token) {
  if (inflight.has(token)) return inflight.get(token);
  const promise = api.post("/auth/qr-redeem", { token }).then((r) => r.data);
  inflight.set(token, promise);
  // Tiny TTL: clear the cache after 60s so a manual page-refresh attempt
  // doesn't get stuck on a stale resolution. (The cached promise itself
  // doesn't expire — only the cache lookup does.)
  setTimeout(() => inflight.delete(token), 60_000);
  return promise;
}

export default function QrRedeem() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = React.useState("loading"); // loading | success | error
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    redeemOnce(token).then(
      (data) => {
        if (!alive) return;
        if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
        if (data.user) setUser(data.user);
        setStatus("success");
        setTimeout(() => nav(data.redirect || "/dashboard", { replace: true }), 600);
      },
      (e) => {
        if (!alive) return;
        const reason =
          e?.response?.data?.detail ||
          (e?.response?.status === 404 ? "token_not_found" : "unknown_error");
        setError(reason);
        setStatus("error");
      }
    );
    return () => { alive = false; };
  }, [token, nav, setUser]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div
        className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white shadow-soft p-8 text-center"
        data-testid="qr-redeem-page"
      >
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-slate-900 text-white mb-5">
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" />
            <circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" />
            <circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" />
          </svg>
        </div>

        {status === "loading" && (
          <>
            <Loader2 className="size-5 mx-auto animate-spin text-slate-400" />
            <h1 className="mt-4 text-lg font-semibold text-slate-900">Connecting your phone…</h1>
            <p className="mt-1.5 text-sm text-slate-500">Verifying your secure one-time link.</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="size-7 mx-auto text-emerald-500" />
            <h1 className="mt-4 text-lg font-semibold text-slate-900">Connected.</h1>
            <p className="mt-1.5 text-sm text-slate-500">Opening your portal…</p>
          </>
        )}

        {status === "error" && (
          <>
            <AlertCircle className="size-7 mx-auto text-rose-500" />
            <h1 className="mt-4 text-lg font-semibold text-slate-900">
              {error === "token_used" && "This link has been used"}
              {error === "token_expired" && "This link has expired"}
              {error === "token_not_found" && "Invalid link"}
              {!["token_used", "token_expired", "token_not_found"].includes(error) && "Couldn't connect"}
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">
              {error === "token_used" && "Each QR code can only be scanned once. Generate a new one from your desktop dashboard."}
              {error === "token_expired" && "QR codes last 10 minutes. Generate a fresh one from your desktop dashboard."}
              {error === "token_not_found" && "This link wasn't recognised. Please ask your colleague to regenerate the QR code."}
              {!["token_used", "token_expired", "token_not_found"].includes(error) && "Please try again, or sign in directly with your email and password."}
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center justify-center w-full rounded-full bg-slate-900 text-white text-sm font-medium h-11 hover:bg-slate-700 transition-colors"
              data-testid="qr-redeem-login-btn"
            >
              Sign in with email
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
